import { UserButton } from "@clerk/clerk-react";
import { Link, Outlet, useLocation } from "@tanstack/react-router";
import {
  ArrowLeftRight,
  Banknote,
  Building2,
  CircleAlert,
  FileClock,
  Radio,
  Receipt,
  Send,
  Store,
  Webhook,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { GlobalSearch } from "~/components/global-search";
import { ThemeToggle } from "~/components/theme-toggle";
import { cn } from "~/lib/utils";

/**
 * Flat navigation. No product switcher and no collapsible tree: this is one
 * product's admin, and every section is one click from every other.
 *
 * The order is the order ops actually works in -- a transaction is what someone
 * arrives with, and the rest are what they check next -- not alphabetical and
 * not schema order. Later milestones fill these in; they are stubbed now so the
 * nav is not rebuilt three times.
 */
const NAV: { to: string; label: string; icon: LucideIcon }[] = [
  { to: "/transactions", label: "Transactions", icon: Receipt },
  { to: "/merchants", label: "Merchants", icon: Building2 },
  { to: "/storefronts", label: "Storefronts", icon: Store },
  { to: "/webhook-events", label: "Webhook Events", icon: Webhook },
  { to: "/payment-conflicts", label: "Payment Conflicts", icon: CircleAlert },
  { to: "/callbacks", label: "Callbacks", icon: Radio },
  { to: "/settlements", label: "Settlements", icon: Banknote },
  { to: "/disbursements", label: "Disbursements", icon: Send },
  { to: "/bridge", label: "Bridge", icon: ArrowLeftRight },
  { to: "/audit-log", label: "Audit Log", icon: FileClock },
];

export function AppShell() {
  return (
    <div className="flex min-h-screen bg-background">
      <aside className="hidden w-60 shrink-0 flex-col border-r border-sidebar-border bg-sidebar md:flex">
        <div className="flex h-14 items-center gap-2 px-4">
          <span className="grid size-7 place-items-center rounded-lg bg-sidebar-primary text-sm font-bold text-sidebar-primary-foreground">
            A
          </span>
          <span className="text-sm font-semibold tracking-tight">Altura Admin</span>
        </div>

        <nav className="flex flex-1 flex-col gap-0.5 p-2">
          {NAV.map((item) => (
            <NavLink key={item.to} {...item} />
          ))}
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center gap-3 border-b px-4">
          {/* The slot PRO-1016 held open, now filled (PRO-1033). */}
          <div className="min-w-0 flex-1">
            <GlobalSearch />
          </div>
          <ThemeToggle />
          <UserButton />
        </header>

        <main className="min-w-0 flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function NavLink({ to, label, icon: Icon }: { to: string; label: string; icon: LucideIcon }) {
  const pathname = useLocation({ select: (l) => l.pathname });
  const active = pathname === to || pathname.startsWith(`${to}/`);

  return (
    <Link
      to={to}
      className={cn(
        "flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium transition-colors",
        active
          ? "bg-sidebar-accent text-sidebar-accent-foreground"
          : "text-muted-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground",
      )}
    >
      <Icon className="size-4 shrink-0" />
      {label}
    </Link>
  );
}
